import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Globe, Instagram, Linkedin, Download } from "lucide-react";
import { LOGO_HD_URL, COMPANY_PROFILE_PDF, SOCIAL_LINKS } from "@/lib/media";

const DESTINATIONS = [
  { to: "/destinations/asia-dmc", label: "Asia DMC Hub" },
  { to: "/dubai-dmc", label: "Dubai" },
  { to: "/singapore-dmc", label: "Singapore" },
  { to: "/malaysia-dmc", label: "Malaysia" },
  { to: "/thailand-dmc", label: "Thailand" },
  { to: "/hong-kong-dmc", label: "Hong Kong" },
  { to: "/vietnam-dmc", label: "Vietnam" },
  { to: "/philippines-dmc", label: "Philippines" },
  { to: "/cambodia-dmc", label: "Cambodia" },
  { to: "/bali-dmc", label: "Bali (Indonesia)" },
  { to: "/laos-dmc", label: "Laos" },
  { to: "/japan-dmc", label: "Japan" },
];

const SERVICES = [
  { to: "/mice-services", label: "MICE Services" },
  { to: "/corporate-travel", label: "Corporate Travel" },
  { to: "/incentive-travel", label: "Incentive Travel" },
];

const COMPANY = [
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

function ColumnList({ overline, items, testIdPrefix }) {
  return (
    <div>
      <div className="overline mb-5">{overline}</div>
      <ul className="space-y-3 text-sm">
        {items.map((item) => (
          <li key={item.to}>
            <Link
              to={item.to}
              className="text-slate-300 hover:text-[#FF5238] transition-colors"
              data-testid={`${testIdPrefix}-${item.label.toLowerCase().replace(/ /g, "-")}`}
            >
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="bg-[#0B192C] text-white mt-32" data-testid="site-footer">
      {/* BRAND BLOCK */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-20 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <img
              src={LOGO_HD_URL}
              alt="Trawelldmc — DMC Dubai and Southeast Asia"
              width="320"
              height="80"
              loading="lazy"
              decoding="async"
              className="h-16 md:h-20 w-auto"
            />
            <p className="text-slate-300 text-sm leading-relaxed max-w-md mt-8">
              Two decades. 140,000+ travelers. Eleven Asian destinations.
              Trawelldmc is the Dubai-headquartered DMC that corporate groups
              and travel agents trust to turn itineraries into unforgettable
              moments.
            </p>
          </div>
          <div className="lg:col-span-6 lg:text-right">
            <div className="overline !text-slate-400 mb-3">Dubai HQ · Since 2004</div>
            <div className="flex flex-col lg:flex-row lg:justify-end gap-3 mb-6">
              <Link to="/inquiry" className="btn-primary inline-flex justify-center" data-testid="footer-cta-proposal">
                Request a proposal
              </Link>
              <a
                href={COMPANY_PROFILE_PDF}
                download="trawelldmc-company-profile.pdf"
                className="btn-secondary inline-flex justify-center !bg-white/10 !border-white/20 !text-white hover:!bg-white/20"
                data-testid="footer-profile-download"
              >
                Company Profile <Download className="h-4 w-4" />
              </a>
              <Link
                to="/mice-case-studies-2025"
                className="btn-secondary inline-flex justify-center !bg-white/10 !border-white/20 !text-white hover:!bg-white/20"
                data-testid="footer-case-studies-pdf"
              >
                5 MICE Case Studies (PDF) <Download className="h-4 w-4" />
              </Link>
            </div>
            <div className="flex gap-3 lg:justify-end">
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="h-11 w-11 inline-flex items-center justify-center border border-white/20 hover:border-[#FF5238] hover:text-[#FF5238] text-white transition-colors"
                data-testid="footer-social-instagram"
                aria-label="Follow Trawelldmc on Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href={SOCIAL_LINKS.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="h-11 w-11 inline-flex items-center justify-center border border-white/20 hover:border-[#FF5238] hover:text-[#FF5238] text-white transition-colors"
                data-testid="footer-social-linkedin"
                aria-label="Connect with Trawelldmc on LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* 4-COLUMN LINK GRID */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-12">
          <ColumnList overline="Destinations" items={DESTINATIONS} testIdPrefix="footer-dest" />
          <ColumnList overline="Services" items={SERVICES} testIdPrefix="footer-svc" />
          <ColumnList overline="Company" items={COMPANY} testIdPrefix="footer-co" />

          <div>
            <div className="overline mb-5">Contact</div>
            <ul className="space-y-4 text-sm text-slate-300">
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-[#FF5238] flex-shrink-0" />
                <span>Business Bay, Dubai<br />United Arab Emirates</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-[#FF5238] flex-shrink-0" />
                <a
                  href="mailto:hello@trawelldmc.com"
                  className="hover:text-[#FF5238] transition-colors"
                  data-testid="footer-email"
                >
                  hello@trawelldmc.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-[#FF5238] flex-shrink-0" />
                <a
                  href="tel:+97158179909"
                  className="hover:text-[#FF5238] transition-colors"
                  data-testid="footer-phone"
                >
                  +971 58 179 9090
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Globe className="h-4 w-4 mt-0.5 text-[#FF5238] flex-shrink-0" />
                trawelldmc.com
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* LEGAL */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <div>© {new Date().getFullYear()} Trawelldmc. All rights reserved.</div>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <a
              href="/trawelldmc-logo-hd.png"
              download="trawelldmc-logo-hd.png"
              className="hover:text-[#FF5238] transition-colors inline-flex items-center gap-1.5"
              data-testid="footer-download-logo"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>
              Download HD Logo
            </a>
            <Link to="/inquiry" className="hover:text-[#FF5238] transition-colors">Agent Registration</Link>
            <Link to="/admin" className="hover:text-[#FF5238] transition-colors" data-testid="footer-admin-link">Agent Dashboard</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
